import { z } from "zod/v4";

import { documentTypeSchema } from "@moment/api-collab/api-types";

import { WorkspaceSchema } from "../workspaces/workspace.types";

export { type DocumentMetadata } from "@moment/api-collab/api-types";

export const DocsListLocalEntrySchema = z.object({
	title: z.string().default(""),
	path: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	type: documentTypeSchema.optional(),
	gitStatus: z.object({}).nullish(),
});
export type DocsListLocalEntry = z.infer<typeof DocsListLocalEntrySchema>;

export const DocsListRemoteCollabEntrySchema = z.object({
	branch: z.string(),
	sha: z.string(),
	status: z.string(),
});
export type DocsListRemoteCollabEntry = z.infer<typeof DocsListRemoteCollabEntrySchema>;

export const DocsListRemoteEntrySchema = z.object({
	id: z.string(),
	title: z.string().default(""),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	type: documentTypeSchema.optional(),
	workspace: WorkspaceSchema,
	// Everything you need to construct Reboot state IDs and connect to collab servers.
	// the key is the branch/sha combination
	collab: z.record(z.string(), DocsListRemoteCollabEntrySchema).default({}),
	// The user's permission level on this document: "write" or "read".
	// "read" means view-only access. The backend maps collaborate to "write"
	// since collaborators now have full write access including git clone.
	permission: z.enum(["write", "read"]).optional(),
});
export type DocsListRemoteEntry = z.infer<typeof DocsListRemoteEntrySchema>;

export const DocumentListItemSchema = z.object({
	id: z.string(),
	path: z.string().default(""),
	title: z.string().default(""),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	remote: DocsListRemoteEntrySchema.nullish(),
});
export type DocumentListItem = z.infer<typeof DocumentListItemSchema>;

export const DocsListItemSchema = z.object({
	id: z.string(),
	// Map of canonicalized path -> local document info.
	local: z.record(z.string(), DocsListLocalEntrySchema).default({}),
	// Map of workspace ID -> remote document info.
	remote: z.record(z.string(), DocsListRemoteEntrySchema).default({}),
});
export type DocsListItem = z.infer<typeof DocsListItemSchema>;

export function docsListItemLocalEntry(document: DocsListItem): DocsListLocalEntry | undefined {
	return Object.values(document.local)[0];
}

export function docsListItemRemoteEntry(document: DocsListItem): DocsListRemoteEntry | undefined {
	return Object.values(document.remote)[0];
}

/**
 * Returns whether the user has viewer (read-only) permission on a document.
 * Accepts either a DocsListItem or a DocsListRemoteEntry directly.
 *
 * Backward compatibility:
 * Older cached remote entries may not have a `permission` field yet. Those should
 * fail closed as viewer/read-only unless the document also has a local entry,
 * since local files are always writable on disk.
 */
export function isViewerPermission(
	entry: DocsListItem | DocsListRemoteEntry | undefined | null
): boolean {
	if (!entry) {
		return false;
	}

	// DocsListItem has a `local` field; DocsListRemoteEntry does not.
	if ("local" in entry) {
		if (docsListItemLocalEntry(entry)) {
			return false;
		}

		const remote = docsListItemRemoteEntry(entry);
		return remote?.permission !== "write";
	}

	return entry.permission !== "write";
}

export function docsListCollabEntry(
	remote: DocsListRemoteEntry | undefined
): DocsListRemoteCollabEntry | undefined {
	if (!remote) {
		return undefined;
	}

	return Object.values(remote.collab)[0];
}

/**
 * Gets the document type from a DocsListItem by checking local entries first, then remote entries.
 * This is necessary because type is stored within the local/remote entries, not at the top level.
 */
export function docsListItemType(document: DocsListItem): string | undefined {
	// Check local entries first (local is the source of truth when present)
	const localEntry = docsListItemLocalEntry(document);
	if (localEntry?.type) {
		return localEntry.type;
	}

	// Fall back to remote entries
	const remoteEntry = docsListItemRemoteEntry(document);
	if (remoteEntry?.type) {
		return remoteEntry.type;
	}

	return undefined;
}
